import { Box, Container, Heading, Link, Stack, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { LandingLayout } from './LandingLayout';
import { LandingHeader } from './components/LandingHeader';
import { LandingFooter } from './components/LandingFooter';
import { Seo } from './seo/Seo';
import { CONTACTS, type ContactPageContent, type LegalLocale } from './content/legal';
import { useContent } from '../content/useContent';
import { RenderedHtml } from '../content/RenderedHtml';

const DESCRIPTION: Record<LegalLocale, string> = {
  en: 'Contact Streamko — the subscription-based business music streaming and audio management service operated by Eva Media LLC.',
  uk: 'Контакти Streamko — сервісу трансляції фонової музики та керування аудіо для бізнесу за передплатою (Eva Media LLC).',
  de: 'Kontaktieren Sie Streamko — den abonnementbasierten Business-Musikstreaming- und Audio-Management-Dienst der Eva Media LLC.',
};

/** Strip spaces from a phone number so the `tel:` href stays dialable. */
const telHref = (phone: string) => `tel:${phone.replace(/\s+/g, '')}`;

/** Structured contact card from the bundled `legal.ts` — prerender/SEO + offline fallback. */
function StructuredContacts({ c }: { c: ContactPageContent }) {
  return (
    <>
      <Text fontSize="xl" fontWeight={600} color="onboarding.deepPlum">
        {c.orgName}
      </Text>
      <Text fontSize="md" color="onboarding.deepPlum">
        {c.registration}
      </Text>
      <Text fontSize="md" color="onboarding.deepPlum">
        {c.address}
      </Text>
      <Link href={`mailto:${c.email}`} fontSize="md" fontWeight={500} color="onboarding.plum" _hover={{ textDecoration: 'underline' }}>
        {c.email}
      </Link>
      <Link href={telHref(c.phone)} fontSize="md" fontWeight={500} color="onboarding.plum" _hover={{ textDecoration: 'underline' }}>
        {c.phone}
      </Link>
    </>
  );
}

/**
 * Standalone, unauthenticated Contacts page. CMS-driven (GET /api/v1/content/contacts)
 * with the bundled `content/legal` contact details as prerender/SEO + offline fallback.
 */
export function ContactsPage() {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const locale: LegalLocale = lang === 'uk' || lang === 'de' ? lang : 'en';
  const c = CONTACTS[locale];
  const { data } = useContent('contacts');
  const title = data?.title || c.title;

  return (
    <LandingLayout header={<LandingHeader />} footer={<LandingFooter />}>
      <Seo title={`${title} — Streamko`} description={DESCRIPTION[locale]} path="/contacts" locale={locale} />
      {/* Title block — full-bleed warm gradient (matches the legal pages). */}
      <Box bgGradient="linear(140deg, #FFFAF9 22%, #FBE5D8 83%)" color="onboarding.deepPlum">
        <Container maxW="1440px" px={{ base: 5, md: 8 }} py={{ base: '56px', lg: '104px' }}>
          <Stack spacing="14px" textAlign="center" align="center">
            <Heading
              as="h1"
              fontFamily="heading"
              fontWeight={700}
              fontSize="display.lg"
              lineHeight={1.1}
              letterSpacing="-0.01em"
              color="onboarding.deepPlum"
            >
              {title}
            </Heading>
            {!data && (
              <Text fontSize="md" color="onboarding.mutedRose" maxW="560px">
                {c.intro}
              </Text>
            )}
          </Stack>
        </Container>
      </Box>

      {/* Contact details card — CMS HTML when available, structured fallback otherwise */}
      <Box as="section" bg="white" color="onboarding.deepPlum">
        <Container maxW="1440px" px={{ base: 5, md: 8 }} py={{ base: 12, lg: 20 }}>
          <Stack
            spacing="10px"
            maxW="520px"
            mx="auto"
            bg="onboarding.creamAlt"
            border="1px solid"
            borderColor="onboarding.peach"
            borderRadius="20px"
            px={{ base: 6, md: 10 }}
            py={{ base: 8, md: 10 }}
          >
            {data?.bodyHtml ? <RenderedHtml html={data.bodyHtml} /> : <StructuredContacts c={c} />}
          </Stack>
        </Container>
      </Box>
    </LandingLayout>
  );
}

export default ContactsPage;
